// ─── Cash flow: daily data, aggregation, affordability, breakeven ─────────────
import {
  MONTH_NAMES, parseDate, toDateStr, mkKey, getDaysInMonth,
  isMonthKey, currSym,
} from "./utils.js";
import {
  getMonthVars, effectivePayoutForMkt, effectiveUnits,
  getActiveMarketplaces, getOverdueExpenses, payoutForMkt,
} from "./marketplaces.js";
import { applyModifiers, applyExpenseModifiers, getVariableCostPerUnit } from "./expenses.js";

// ─── Affordability helpers ────────────────────────────────────────────────────
function findDailyEntry(dailyData, date) {
  if (!date || !dailyData?.length) return null;
  const y = date.getFullYear(), m = date.getMonth() + 1, d = date.getDate();
  return dailyData.find(e => e.year === y && e.month === m && e.day === d) || null;
}

function findNearbyExpenses(dailyData, date, rangeDays = 3) {
  if (!date || !dailyData?.length) return [];
  const targetMs = date.getTime();
  const rangeMs = rangeDays * 86_400_000;
  return dailyData
    .filter(e => {
      const ms = new Date(e.year, e.month - 1, e.day).getTime();
      return Math.abs(ms - targetMs) <= rangeMs && ms !== targetMs && e.expenses > 0;
    })
    .map(e => ({ date: `${e.day}/${e.month}`, expenses: e.expenses }))
    .slice(0, 5);
}

export function findOptimalPaymentDates(dailyData, originalDate, paymentAmount, bufferThreshold = 0, rangeDays = 14) {
  if (!originalDate || !dailyData?.length) return null;
  const origMs = originalDate.getTime();
  const candidates = dailyData
    .filter(e => {
      const ms = new Date(e.year, e.month - 1, e.day).getTime();
      return Math.abs(ms - origMs) <= rangeDays * 86_400_000 && ms !== origMs;
    })
    .map(e => ({
      date: new Date(e.year, e.month - 1, e.day),
      dateStr: `${e.day}/${e.month}/${e.year}`,
      balanceAfter: e.balance + paymentAmount - paymentAmount,
      balanceWithPayment: (e.balance + (findDailyEntry(dailyData, originalDate)?.expenses || 0)) - paymentAmount,
    }))
    .sort((a, b) => b.balanceWithPayment - a.balanceWithPayment);
  if (!candidates.length) return null;
  const best = candidates[0];
  return best.balanceWithPayment > bufferThreshold ? { date: best.date, dateStr: best.dateStr, balance: best.balanceWithPayment } : null;
}

export function projectOrderIncome({ orderUnits, inStockDate, marketplaces, months }) {
  if (!orderUnits || !inStockDate || !marketplaces?.length) return null;
  // Pass { months } so the per-marketplace payout comes from the realized
  // cascade (API payouts → net revenue → list price) instead of the raw
  // product.payout list price. Window defaults to 30d.
  const payoutCtx = { months };
  const weightedPayout = marketplaces.reduce((s, mkt) =>
    s + (Number(mkt.pctOfTotal) || 0) * effectivePayoutForMkt(mkt, payoutCtx, 30), 0);
  if (weightedPayout <= 0) return null;

  let remaining = orderUnits;
  const breakdown = [];
  let y = inStockDate.getFullYear(), m = inStockDate.getMonth() + 1;
  while (remaining > 0 && breakdown.length < 24) {
    const key = mkKey(y, m);
    const target = months?.[key]?.target || 0;
    const sold = Math.min(remaining, target || remaining);
    const income = Math.round(sold * weightedPayout);
    breakdown.push({ month: key, units: sold, income });
    remaining -= sold;
    m++; if (m > 12) { m = 1; y++; }
  }
  const totalIncome = breakdown.reduce((s, b) => s + b.income, 0);
  return { weightedPayout: Math.round(weightedPayout * 100) / 100, totalIncome, breakdown, monthsToRecoup: breakdown.length };
}

export function analyzeAffordability({ dailyData, cashEvents, bufferThreshold = 0, marketplaces, months, orderUnits, inStockDate }) {
  if (!dailyData?.length || !cashEvents?.length) return null;
  const threshold = bufferThreshold || 0;

  const payments = cashEvents.map(evt => {
    const date = evt.date instanceof Date ? evt.date : parseDate(evt.dateStr || evt.date);
    if (!date) return { ...evt, balanceBefore: null, balanceAfter: null, aboveBuffer: null, shortfall: 0, suggestedDate: null, nearbyExpenses: [] };

    const entry = findDailyEntry(dailyData, date);
    if (!entry) return { ...evt, date, balanceBefore: null, balanceAfter: null, aboveBuffer: null, shortfall: 0, suggestedDate: null, nearbyExpenses: [] };

    const balanceAfter = Math.round(entry.balance);
    const balanceBefore = Math.round(entry.balance + evt.amount);
    const aboveBuffer = balanceAfter >= threshold;
    const shortfall = aboveBuffer ? 0 : Math.round(threshold - balanceAfter);
    const nearbyExpenses = findNearbyExpenses(dailyData, date);
    const suggestedDate = !aboveBuffer ? findOptimalPaymentDates(dailyData, date, evt.amount, threshold) : null;

    return {
      label: evt.label || "Payment",
      date,
      dateStr: toDateStr(date.getFullYear(), date.getMonth() + 1, date.getDate()),
      amount: Math.round(evt.amount),
      balanceBefore, balanceAfter, aboveBuffer, shortfall,
      nearbyExpenses, suggestedDate,
      paid: evt.paid || false,
    };
  }).filter(Boolean);

  const unpaid = payments.filter(p => !p.paid && p.balanceAfter !== null);
  const failing = unpaid.filter(p => !p.aboveBuffer);
  const lowestEntry = unpaid.length ? unpaid.reduce((a, b) => (a.balanceAfter ?? Infinity) < (b.balanceAfter ?? Infinity) ? a : b) : null;
  const totalCost = cashEvents.reduce((s, e) => s + (e.amount || 0), 0);

  const incomeProjection = projectOrderIncome({ orderUnits, inStockDate, marketplaces, months });

  const suggestions = [];
  for (const p of failing) {
    if (p.suggestedDate) {
      suggestions.push({ type: "reschedule", message: `Move "${p.label}" to ${p.suggestedDate.dateStr} — balance would be ${currSym()}${p.suggestedDate.balance.toLocaleString()} (above buffer)`, detail: p });
    }
    if (p.amount > (p.balanceBefore || 0) * 0.5) {
      const half = Math.round(p.amount / 2);
      suggestions.push({ type: "split", message: `Split "${p.label}" into 2 payments of ${currSym()}${half.toLocaleString()} spaced 2 weeks apart`, detail: p });
    }
  }

  return {
    payments,
    overall: {
      canAfford: failing.length === 0,
      lowestPoint: lowestEntry ? { date: lowestEntry.date, balance: lowestEntry.balanceAfter, label: lowestEntry.label } : null,
      totalCost: Math.round(totalCost),
      expectedIncome: incomeProjection?.totalIncome || 0,
      netCostAfterIncome: Math.round(totalCost - (incomeProjection?.totalIncome || 0)),
    },
    suggestions,
    incomeProjection,
  };
}

export function analyzeDecisionAffordability({ decision, dailyData, recurringExpenses, modifiers, months, marketplaces, bufferThreshold = 0, today }) {
  if (!decision || !dailyData?.length) return null;
  const decId = decision.id;
  const threshold = bufferThreshold || 0;

  const linkedRecurring = (recurringExpenses || []).filter(r => r.decisionId === decId);
  const linkedModifiers = (modifiers || []).filter(m => m.decisionId === decId);

  const oneTimeCosts = [];
  const oneTimeIncome = [];
  for (const [key, mData] of Object.entries(months || {})) {
    if (!key.match(/^\d{4}-\d{1,2}$/)) continue;
    for (const exp of (mData.expenses || [])) {
      if (exp.decisionId === decId) oneTimeCosts.push({ ...exp, key });
    }
    for (const inc of (mData.incomeItems || [])) {
      if (inc.decisionId === decId) oneTimeIncome.push({ ...inc, key });
    }
  }

  const monthlyExpenses = linkedRecurring.reduce((s, r) => s + (r.amount || 0), 0);

  const currentKey = mkKey(today.getFullYear(), today.getMonth() + 1);
  const target = months?.[currentKey]?.target || 0;
  const payoutCtx = { months };
  const weightedPayout = marketplaces.reduce((s, mkt) =>
    s + (Number(mkt.pctOfTotal) || 0) * effectivePayoutForMkt(mkt, payoutCtx, 30), 0);
  let monthlyIncomeChange = 0;
  for (const mod of linkedModifiers) {
    if (mod.type === "payout") monthlyIncomeChange += target * weightedPayout * (mod.value || 0);
    else if (mod.type === "units") monthlyIncomeChange += target * (mod.value || 0) * weightedPayout;
  }

  const netMonthly = Math.round(monthlyIncomeChange - monthlyExpenses);
  const oneTimeTotalCost = oneTimeCosts.reduce((s, e) => s + (e.amount || 0), 0);
  const oneTimeTotalIncome = oneTimeIncome.reduce((s, e) => s + (e.amount || 0), 0);

  const impactBreakdown = [];
  for (const r of linkedRecurring) {
    impactBreakdown.push({ type: "recurring_expense", label: r.name || r.what || "Recurring cost", monthlyAmount: r.amount || 0, direction: "out" });
  }
  for (const mod of linkedModifiers) {
    const pct = Math.round((mod.value || 0) * 100);
    const est = mod.type === "payout" ? target * weightedPayout * (mod.value || 0) : target * (mod.value || 0) * weightedPayout;
    impactBreakdown.push({ type: "modifier", label: `${mod.type === "payout" ? "Payout" : mod.type === "units" ? "Units" : "Stop selling"} ${pct >= 0 ? "+" : ""}${pct}%`, monthlyAmount: Math.round(est), direction: est >= 0 ? "in" : "out" });
  }
  for (const e of oneTimeCosts) {
    impactBreakdown.push({ type: "one_time_cost", label: e.what || "One-time cost", monthlyAmount: e.amount || 0, direction: "out" });
  }
  for (const e of oneTimeIncome) {
    impactBreakdown.push({ type: "one_time_income", label: e.what || "Cash in", monthlyAmount: e.amount || 0, direction: "in" });
  }

  const decDate = decision.date ? parseDate(typeof decision.date === "object" ? `${decision.date.day||1}.${decision.date.month||1}.${decision.date.year||today.getFullYear()}` : decision.date) : today;
  const futureData = dailyData.filter(d => {
    const dMs = new Date(d.year, d.month - 1, d.day).getTime();
    return dMs >= (decDate?.getTime() || 0);
  });
  const lowestPoint = futureData.length ? futureData.reduce((a, b) => a.balance < b.balance ? a : b) : null;
  const breachesBuffer = futureData.some(d => d.balance < threshold);

  return {
    canAfford: !breachesBuffer,
    monthlyExpenses, monthlyIncomeChange: Math.round(monthlyIncomeChange), netMonthly,
    oneTimeTotalCost: Math.round(oneTimeTotalCost), oneTimeTotalIncome: Math.round(oneTimeTotalIncome),
    impactBreakdown,
    lowestPoint: lowestPoint ? { date: new Date(lowestPoint.year, lowestPoint.month - 1, lowestPoint.day), balance: Math.round(lowestPoint.balance) } : null,
    breachDate: breachesBuffer ? (() => { const b = futureData.find(d => d.balance < threshold); return b ? new Date(b.year, b.month - 1, b.day) : null; })() : null,
    bufferThreshold: threshold,
  };
}

// ─── Minimum units to cover all expenses this month ─────────────────────────
export function computeBreakevenUnits(key, marketplaces, months, deliveryExpenses, orderExpenses, recurringItems, opts = {}) {
  const { fbmSettings, inventorySummary, today, bufferThreshold = 0 } = opts;
  const mData = months[key] || {};
  const mv = getMonthVars(key, marketplaces, months);

  let payoutPerUnit = 0;
  const payoutCtx = { months };
  for (const mkt of mv.marketplaces) {
    payoutPerUnit += mkt.pctOfTotal * effectivePayoutForMkt(mkt, payoutCtx, 30);
  }
  if (payoutPerUnit <= 0) return null;

  const variableCostPerUnit = getVariableCostPerUnit(mv.marketplaces, fbmSettings, inventorySummary);
  const contributionMarginPerUnit = payoutPerUnit - variableCostPerUnit;
  if (contributionMarginPerUnit <= 0) return { minUnits: Infinity, totalExpenses: 0, payoutPerUnit: Math.round(payoutPerUnit * 100) / 100, contributionMarginPerUnit: 0 };

  let totalExpenses = 0;

  for (const exp of (mData.expenses || [])) {
    if (exp.paid) continue;
    let amt = exp.amount || 0;
    if (exp.partialAmount !== undefined) amt = Math.max(0, amt - exp.partialAmount);
    totalExpenses += amt;
  }

  for (const exp of ((deliveryExpenses || {})[key] || [])) {
    const dovr = (mData.deliveryOverrides || {})[exp.id] || {};
    if (dovr.paid || dovr.deleted) continue;
    let amt = exp.amount || 0;
    if (dovr.partialAmount !== undefined) amt = Math.max(0, amt - dovr.partialAmount);
    totalExpenses += amt;
  }

  for (const exp of ((orderExpenses || {})[key] || [])) {
    if ((mData.orderExpPaid || {})[exp.id]) continue;
    let amt = exp.amount || 0;
    const paid = (mData.orderExpPartial || {})[exp.id];
    if (paid !== undefined) amt = Math.max(0, amt - paid);
    totalExpenses += amt;
  }

  for (const item of ((recurringItems || {})[key] || [])) {
    if (item.isIncome) continue;
    const ov = (mData.recurringOverrides || {})[item.id||item.recurringId] || {};
    if (ov.paid || ov.deleted) continue;
    let amt = ov.amount !== undefined ? ov.amount : (item.amount || 0);
    if (ov.partialAmount !== undefined) amt = Math.max(0, amt - ov.partialAmount);
    totalExpenses += amt;
  }

  let incomeAlreadyEarned = 0;
  if (today && mData.useActualSold && mData.actualSold > 0) {
    incomeAlreadyEarned = mData.actualSold * payoutPerUnit;
  }

  const remainingDeficit = Math.max(0, totalExpenses + bufferThreshold - incomeAlreadyEarned);
  const minUnits = remainingDeficit <= 0 ? 0 : Math.ceil(remainingDeficit / contributionMarginPerUnit);
  return {
    minUnits,
    totalExpenses: Math.round(totalExpenses),
    payoutPerUnit: Math.round(payoutPerUnit * 100) / 100,
    contributionMarginPerUnit: Math.round(contributionMarginPerUnit * 100) / 100,
    incomeAlreadyEarned: Math.round(incomeAlreadyEarned),
  };
}

// ─── Core daily data computation ─────────────────────────────────────────────
export function computeDailyData({
  marketplaces,
  months,
  deliveryExpenses,
  orderExpenses,
  recurringItems,
  incomeOverrides,
  availableToday,
  today,
  visibleYears,
  chartFrom,
  modifiers = [],
}) {
  const allYears = visibleYears.slice().sort();
  const allDays = [];
  for (const year of allYears) {
    for (let m = 1; m <= 12; m++) {
      const dim = getDaysInMonth(year, m);
      for (let d = 1; d <= dim; d++) allDays.push({ year, month: m, day: d });
    }
  }
  const N = allDays.length;
  const incomeReceived = new Array(N + 365).fill(0);
  const processed = {};
  const todayY = today.getFullYear(), todayM = today.getMonth() + 1, todayD = today.getDate();
  // Realized-payout context: passed through every effectivePayoutForMkt call
  // below so the daily income projection uses Shopify Payments / Amazon
  // settlement cash instead of list prices. 30d window is the default share
  // window; if we later expose a per-brand override this is the single place
  // to read it from.
  const payoutCtx = { months };
  const payoutWin = 30;

  for (let i = 0; i < N; i++) {
    const { year, month } = allDays[i];
    const key = mkKey(year, month);
    if (processed[key] !== undefined) continue;
    const mv = getMonthVars(key, marketplaces, months);
    const modifiedMkts = applyModifiers(mv.marketplaces, modifiers, key);
    const dim = getDaysInMonth(year, month);
    const isPast = year < todayY || (year === todayY && month < todayM);
    const isCurrent = year === todayY && month === todayM;
    const mData = months[key] || {};
    const hasActual = mData.useActualSold && mData.actualSold > 0;

    if (isPast && !hasActual) { processed[key] = {}; continue; }

    // Per-marketplace: find the LAST day covered by API payouts in this month.
    // For days AFTER that last payout, we still project income from targets.
    // For days AT OR BEFORE, the API payout is the truth — no projection.
    //
    // Example: today=Apr 10, API has payouts on Apr 1 and Apr 14.
    //   - Days 1-14: covered by API payouts (no projection)
    //   - Days 15-30: projected from targets (not covered by any API payout)
    //
    // For PAST months that are fully covered by API payouts (last payout
    // date >= last day of month), the entire month is suppressed from
    // projection. For FUTURE months with no API payouts, the full month
    // is projected normally.
    const apiLastDayByMkt = {};
    for (const inc of (mData.incomeItems || [])) {
      if (!inc._apiSourced || !inc.marketplaceId) continue;
      let payDay = 0;
      if (inc.date) {
        const d = parseDate(inc.date);
        if (d && d.getMonth() + 1 === month && d.getFullYear() === year) payDay = d.getDate();
      }
      if (!payDay) payDay = 1; // fallback: assume start of month
      apiLastDayByMkt[inc.marketplaceId] = Math.max(apiLastDayByMkt[inc.marketplaceId] || 0, payDay);
    }

    if (isPast && !hasActual) { processed[key] = {}; continue; }

    if (isPast && hasActual) {
      const actualPerDay = mData.actualSold;
      processed[key] = { firstDay: 1 };
      for (const mkt of modifiedMkts) {
        const apiLastDay = apiLastDayByMkt[mkt.id] || 0;
        if (apiLastDay >= dim) continue; // fully covered by API — skip projection
        // Partially covered: project from (apiLastDay+1) to end of month
        const projFirstDay = apiLastDay > 0 ? apiLastDay + 1 : 1;
        const projDays = dim - projFirstDay + 1;
        const perDay = actualPerDay * mkt.pctOfTotal * effectivePayoutForMkt(mkt, payoutCtx, payoutWin) / dim;
        processed[key][mkt.id] = { perDay, delay: Math.round(mkt.payoutDelay), firstDay: projFirstDay };
      }
      continue;
    }

    const units = effectiveUnits(key, months, today);
    const daysLeft = isCurrent ? Math.max(1, dim - todayD) : dim;
    const firstDay = isCurrent ? todayD : 1;
    processed[key] = { firstDay };

    if (isCurrent && hasActual) {
      for (const mkt of modifiedMkts) {
        const apiLastDay = apiLastDayByMkt[mkt.id] || 0;
        // Project only from max(apiLastDay+1, todayD) onwards
        const projStart = Math.max(apiLastDay > 0 ? apiLastDay + 1 : 1, todayD);
        const projDays = Math.max(1, dim - projStart + 1);
        if (apiLastDay >= dim) continue; // fully covered
        const perDayActual = apiLastDay >= todayD ? 0 : mData.actualSold * mkt.pctOfTotal * effectivePayoutForMkt(mkt, payoutCtx, payoutWin) / Math.max(1, todayD);
        const perDayProjected = units * mkt.pctOfTotal * effectivePayoutForMkt(mkt, payoutCtx, payoutWin) / projDays;
        if (apiLastDay >= todayD) {
          // API covers up to or past today — only project future days
          processed[key][mkt.id] = { perDay: perDayProjected, delay: Math.round(mkt.payoutDelay), firstDay: projStart };
        } else {
          processed[key][mkt.id] = {
            perDay: perDayProjected, delay: Math.round(mkt.payoutDelay), firstDay: projStart,
            backfillPerDay: perDayActual, backfillFirst: 1, backfillLast: todayD - 1,
          };
        }
      }
    } else {
      for (const mkt of modifiedMkts) {
        const apiLastDay = apiLastDayByMkt[mkt.id] || 0;
        if (apiLastDay >= dim) continue; // fully covered
        const projStart = apiLastDay > 0 ? apiLastDay + 1 : firstDay;
        const projDays = Math.max(1, dim - projStart + 1);
        const perDay = units * mkt.pctOfTotal * effectivePayoutForMkt(mkt, payoutCtx, payoutWin) / projDays;
        processed[key][mkt.id] = { perDay, delay: Math.round(mkt.payoutDelay), firstDay: projStart };
      }
    }
  }

  for (let i = 0; i < N; i++) {
    const { year, month, day } = allDays[i];
    const key = mkKey(year, month);
    const pm = processed[key];
    if (!pm) continue;
    for (const mktId of Object.keys(pm)) {
      if (mktId === "firstDay") continue;
      const info = pm[mktId];
      if (!info) continue;
      if (info.backfillPerDay && day >= info.backfillFirst && day <= info.backfillLast) {
        const di = i + info.delay;
        if (di < incomeReceived.length) incomeReceived[di] += info.backfillPerDay;
      }
      if (day >= info.firstDay) {
        const di = i + info.delay;
        if (di < incomeReceived.length) incomeReceived[di] += info.perDay;
      }
    }
  }

  const monthAutoIncome = {};
  for (let i = 0; i < N; i++) {
    const { year, month } = allDays[i];
    const key = mkKey(year, month);
    monthAutoIncome[key] = (monthAutoIncome[key] || 0) + (incomeReceived[i] || 0);
  }

  const dayWeekKey = new Array(N).fill(null);
  let wStart = 0;
  while (wStart < N) {
    const d = new Date(allDays[wStart].year, allDays[wStart].month - 1, allDays[wStart].day);
    if (d.getDay() === 1) break;
    wStart++;
  }
  let wi = wStart > 0 ? 2 : 1;
  if (wStart > 0) for (let i = 0; i < wStart; i++) dayWeekKey[i] = "week_1";
  for (let i = wStart; i < N; i += 7) {
    const wk = `week_${wi++}`;
    for (let j = i; j < Math.min(i + 7, N); j++) dayWeekKey[j] = wk;
  }

  const weekAutoIncome = {};
  for (let i = 0; i < N; i++) {
    const wk = dayWeekKey[i];
    if (wk) weekAutoIncome[wk] = (weekAutoIncome[wk] || 0) + (incomeReceived[i] || 0);
  }

  const monthScale = {};
  for (const [k, v] of Object.entries(incomeOverrides)) {
    if (v == null || k.startsWith("week_")) continue;
    const a = monthAutoIncome[k] || 0;
    monthScale[k] = a > 0 ? Math.min(v / a, 100) : 0; // cap to prevent runaway scaling
  }

  const expByDate = {};
  function addExp(y, m, d, amt) {
    const dk = `${y}-${m}-${d}`;
    expByDate[dk] = (expByDate[dk] || 0) + amt;
  }

  for (const key of Object.keys(months)) {
    if (!isMonthKey(key)) continue;
    const [year, m] = key.split("-").map(Number);
    const mData = months[key] || { expenses: [] };
    const dim = getDaysInMonth(year, m);

    for (const exp of (mData.expenses || [])) {
      if (exp.paid) continue;
      let amt = exp.amount || 0;
      if (exp.partialAmount !== undefined) amt = Math.max(0, amt - exp.partialAmount);
      if (amt <= 0) continue;
      if (exp.date) {
        const d = parseDate(exp.date);
        if (d && d.getFullYear() === year && d.getMonth() + 1 === m) addExp(year, m, d.getDate(), amt);
      } else addExp(year, m, 1, amt);
    }

    for (const exp of (deliveryExpenses[key] || [])) {
      const dovr = (mData.deliveryOverrides || {})[exp.id] || {};
      if (dovr.paid || dovr.deleted) continue;
      let baseAmt = applyExpenseModifiers(exp.amount, exp.sourceId || exp.id, "fulfilment", modifiers, key);
      if (dovr.partialAmount !== undefined) baseAmt = Math.max(0, baseAmt - dovr.partialAmount);
      if (baseAmt <= 0) continue;
      const ds = dovr.delayedDate || dovr.date || exp.date;
      if (ds) {
        const d = parseDate(ds);
        if (d) addExp(d.getFullYear(), d.getMonth() + 1, d.getDate(), baseAmt);
      }
    }

    for (const exp of (orderExpenses[key] || [])) {
      const paid = (mData.orderExpPaid || {})[exp.id];
      if (paid) continue;
      const amountPaid = (mData.orderExpPartial || {})[exp.id];
      const amt = amountPaid !== undefined ? Math.max(0, exp.amount - amountPaid) : exp.amount;
      if (amt <= 0) continue;
      const delayed = (mData.orderExpDelayed || {})[exp.id];
      const dateStr = (delayed && delayed.newDate) ? delayed.newDate : exp.date;
      if (dateStr) {
        const d = parseDate(dateStr);
        if (d) addExp(d.getFullYear(), d.getMonth() + 1, d.getDate(), amt);
      } else addExp(year, m, 1, amt);
    }

    for (const exp of (recurringItems[key] || [])) {
      if (exp.isIncome) continue;
      const ov = (mData.recurringOverrides || {})[exp.id||exp.recurringId] || {};
      if (ov.deleted || ov.paid) continue;
      let baseAmt = ov.amount !== undefined ? ov.amount : exp.amount;
      const modAmt = applyExpenseModifiers(baseAmt, exp.recurringId, "partner", modifiers, key);
      let finalAmt = modAmt;
      if (ov.partialAmount !== undefined) finalAmt = Math.max(0, modAmt - ov.partialAmount);
      if (finalAmt <= 0) continue;
      const dateStr = ov.delayedDate || exp.date;
      if (dateStr) {
        const d = parseDate(dateStr);
        if (d) addExp(d.getFullYear(), d.getMonth() + 1, d.getDate(), finalAmt);
      } else addExp(year, m, 1, finalAmt);
    }
  }

  const incomeByDate = {};
  function addInc(y, m, d, amt) {
    const dk = `${y}-${m}-${d}`;
    incomeByDate[dk] = (incomeByDate[dk] || 0) + amt;
  }
  for (const key of Object.keys(months)) {
    if (!isMonthKey(key)) continue;
    const [year, m] = key.split("-").map(Number);
    for (const item of (recurringItems[key] || [])) {
      if (!item.isIncome) continue;
      const mData = months[key] || {};
      const ov = (mData.recurringOverrides || {})[item.id||item.recurringId] || {};
      if (ov.deleted) continue;
      const amt = ov.amount !== undefined ? ov.amount : item.amount;
      if (item.date) {
        const d = parseDate(item.date);
        if (d) addInc(d.getFullYear(), d.getMonth() + 1, d.getDate(), amt);
      } else addInc(year, m, 1, amt);
    }
    const mData = months[key] || {};
    for (const item of (mData.incomeItems || [])) {
      let amt = Number(item.amount) || 0;
      if (amt <= 0) continue;
      // Apply decision modifiers (income_pct, units_pct, stop_selling) to API-sourced items.
      // Sort modifiers deterministically (order → createdAt → id) so the
      // result doesn't depend on array insertion order. All ops are
      // multiplicative today, but sorting is defense against a future
      // non-commutative modifier type.
      if (item._apiSourced && item.marketplaceId && modifiers && modifiers.length > 0) {
        const sortedMods = [...modifiers].sort((a, b) =>
          ((a?.order ?? 0) - (b?.order ?? 0)) ||
          String(a?.createdAt || "").localeCompare(String(b?.createdAt || "")) ||
          String(a?.id || "").localeCompare(String(b?.id || ""))
        );
        for (const mod of sortedMods) {
          if (mod.type !== "payout" && mod.type !== "units") continue;
          const activeKeys = Array.isArray(mod.monthKeys) && mod.monthKeys.length > 0
            ? mod.monthKeys : mod.monthKey ? [mod.monthKey] : [];
          if (activeKeys.length === 0 || !activeKeys.includes(key)) continue;
          const scope = mod.scope || "all";
          if (scope !== "all" && !(scope === "marketplace" && mod.marketplaceId === item.marketplaceId)) continue;
          const pct = mod.value || 0;
          amt = amt * (1 + pct);
        }
      }
      if (amt <= 0) continue;
      if (item.date) {
        const d = parseDate(item.date);
        if (d && d.getFullYear() === year && d.getMonth() + 1 === m) addInc(year, m, d.getDate(), amt);
      } else addInc(year, m, 1, amt);
    }
  }

  const days = [];
  let balance = availableToday;
  const isBeforeTodayFn = (year, month, day) =>
    year < todayY || (year === todayY && month < todayM) || (year === todayY && month === todayM && day < todayD);

  for (let i = 0; i < N; i++) {
    const { year, month, day } = allDays[i];
    const key = mkKey(year, month);
    const wk = dayWeekKey[i];
    const beforeToday = isBeforeTodayFn(year, month, day);

    let income = incomeReceived[i] || 0;
    if (monthScale[key] !== undefined) income *= monthScale[key];
    else if (wk && incomeOverrides[wk] != null) {
      const aw = weekAutoIncome[wk] || 0;
      income *= aw > 0 ? incomeOverrides[wk] / aw : 0;
    }
    income += incomeByDate[`${year}-${month}-${day}`] || 0;

    const expenses = expByDate[`${year}-${month}-${day}`] || 0;

    if (chartFrom !== "today" || !beforeToday) {
      balance = balance + income - expenses;
    }

    days.push({
      year, month, day,
      label: `${day} ${MONTH_NAMES[month - 1]}`,
      shortLabel: `${day}/${month}`,
      fullLabel: `${day} ${MONTH_NAMES[month - 1]} ${year}`,
      income, expenses, balance,
      key, weekKey: wk, isBeforeToday: beforeToday,
    });
  }
  return days;
}

// ─── Aggregate to weekly / monthly ───────────────────────────────────────────
export function aggregateWeekly(dailyData, weekStartDay = 1) {
  if (!dailyData.length) return [];
  const weeks = [];
  let wStart = 0;
  while (wStart < dailyData.length) {
    const d = new Date(dailyData[wStart].year, dailyData[wStart].month - 1, dailyData[wStart].day);
    if (d.getDay() === weekStartDay) break;
    wStart++;
  }
  if (wStart > 0) {
    const s = dailyData.slice(0, wStart);
    const first = s[0], last = s[s.length - 1];
    const startDate = new Date(first.year, first.month-1, first.day);
    const endDate   = new Date(last.year,  last.month-1,  last.day);
    const MN_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const dateRange = startDate.getMonth() === endDate.getMonth()
      ? `${startDate.getDate()}–${endDate.getDate()}`
      : `${startDate.getDate()}/${startDate.getMonth()+1}–${endDate.getDate()}/${endDate.getMonth()+1}`;
    const tooltipRange = startDate.getMonth() === endDate.getMonth()
      ? `${MN_SHORT[startDate.getMonth()]} ${startDate.getDate()}-${endDate.getDate()}`
      : `${MN_SHORT[startDate.getMonth()]} ${startDate.getDate()}-${MN_SHORT[endDate.getMonth()]} ${endDate.getDate()}`;
    weeks.push({
      label: `W1`,
      fullLabel: `W1: ${tooltipRange}`,
      income: s.reduce((a,d)=>a+d.income, 0),
      expenses: s.reduce((a,d)=>a+d.expenses, 0),
      balance: last.balance,
      overrideKey: "week_1",
      year: last.year, month: last.month, day: last.day,
      isBeforeToday: last.isBeforeToday,
    });
  }
  let wi2 = weeks.length + 1;
  for (let i = wStart; i < dailyData.length; i += 7) {
    const s = dailyData.slice(i, Math.min(i+7, dailyData.length));
    if (!s.length) break;
    const first = s[0], last = s[s.length-1];
    const startDate = new Date(first.year, first.month-1, first.day);
    const endDate   = new Date(last.year,  last.month-1,  last.day);
    const MN_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const dateRange = startDate.getMonth() === endDate.getMonth()
      ? `${startDate.getDate()}–${endDate.getDate()}`
      : `${startDate.getDate()}/${startDate.getMonth()+1}–${endDate.getDate()}/${endDate.getMonth()+1}`;
    const tooltipRange = startDate.getMonth() === endDate.getMonth()
      ? `${MN_SHORT[startDate.getMonth()]} ${startDate.getDate()}-${endDate.getDate()}`
      : `${MN_SHORT[startDate.getMonth()]} ${startDate.getDate()}-${MN_SHORT[endDate.getMonth()]} ${endDate.getDate()}`;
    weeks.push({
      label: `W${wi2}`,
      fullLabel: `W${wi2}: ${tooltipRange}`,
      income: s.reduce((a,d)=>a+d.income, 0),
      expenses: s.reduce((a,d)=>a+d.expenses, 0),
      balance: last.balance,
      overrideKey: `week_${wi2}`,
      year: last.year, month: last.month, day: last.day,
      isBeforeToday: last.isBeforeToday,
    });
    wi2++;
  }
  return weeks;
}

export function aggregateMonthly(dailyData, visibleYears) {
  const result = [];
  for (const year of visibleYears.slice().sort()) {
    for (let m = 1; m <= 12; m++) {
      const mDays = dailyData.filter(d => d.year === year && d.month === m);
      if (!mDays.length) continue;
      const key = mkKey(year, m);
      result.push({
        label: `${MONTH_NAMES[m - 1]} '${String(year).slice(2)}`,
        fullLabel: `${MONTH_NAMES[m - 1]} ${year}`,
        year, month: m,
        income: mDays.reduce((s, d) => s + d.income, 0),
        expenses: mDays.reduce((s, d) => s + d.expenses, 0),
        balance: mDays[mDays.length - 1].balance,
        isBeforeToday: mDays[mDays.length - 1].isBeforeToday,
        key,
      });
    }
  }
  return result;
}

// ─── Next payment due ─────────────────────────────────────────────────────────
export function getNextPaymentDue(months, deliveryExpenses, orderExpenses, recurringItems, today) {
  const todayTime = today.getTime();
  let nearest = null;

  function check(dateStr, amount, label, id, monthKey) {
    if (amount <= 0) return;
    const d = parseDate(dateStr);
    if (!d || d.getTime() < todayTime) return;
    if (!nearest || d.getTime() < nearest.date.getTime()) {
      nearest = { date: d, amount, label, id: id || null, monthKey: monthKey || null };
    }
  }

  for (const [key, exps] of Object.entries(deliveryExpenses)) {
    const mData = months[key] || {};
    for (const exp of exps) {
      const dovr = (mData.deliveryOverrides || {})[exp.id] || {};
      if (dovr.paid || dovr.deleted) continue;
      let amt = exp.amount || 0;
      if (dovr.partialAmount !== undefined) amt = Math.max(0, amt - dovr.partialAmount);
      check(dovr.delayedDate || dovr.date || exp.date, amt, exp.what || "📦 Fulfilment", exp.id, key);
    }
  }

  for (const [key, exps] of Object.entries(orderExpenses)) {
    const mData = months[key] || {};
    for (const exp of exps) {
      if ((mData.orderExpPaid || {})[exp.id]) continue;
      let amt = exp.amount || 0;
      const amountPaid = (mData.orderExpPartial || {})[exp.id];
      if (amountPaid !== undefined) amt = Math.max(0, amt - amountPaid);
      const delayed = (mData.orderExpDelayed || {})[exp.id];
      check((delayed && delayed.newDate) ? delayed.newDate : exp.date, amt, exp.what || "💳 Order payment", exp.id, key);
    }
  }

  for (const [key, items] of Object.entries(recurringItems)) {
    const mData = months[key] || {};
    for (const item of items) {
      if (item.isIncome) continue;
      const ov = (mData.recurringOverrides || {})[item.id||item.recurringId] || {};
      if (ov.paid || ov.deleted) continue;
      let amt = ov.amount !== undefined ? ov.amount : (item.amount || 0);
      if (ov.partialAmount !== undefined) amt = Math.max(0, amt - ov.partialAmount);
      check(ov.delayedDate || item.date, amt, item.what || "🔄 Recurring", item.recurringId, key);
    }
  }

  for (const [key, mData] of Object.entries(months)) {
    if (!isMonthKey(key)) continue;
    for (const exp of (mData.expenses || [])) {
      if (exp.paid) continue;
      let amt = exp.amount || 0;
      if (exp.partialAmount !== undefined) amt = Math.max(0, amt - exp.partialAmount);
      check(exp.date, amt, exp.what || "✏️ Manual expense", exp.id, key);
    }
  }

  return nearest;
}

// ─── Runway calculation ───────────────────────────────────────────────────────
// Returns the number of days until cash drops below `bufferThreshold`.
// Infinity means "no breach in the forecast window" (semantic signal — callers
// either render "12+ months" or clamp to a sentinel like 9999 for JSON).
//
// Previously when `todayEntry` was missing this function *also* returned
// Infinity, silently masking real breaches on any forecast that didn't
// include today's date. Now we compute the delta from `today` directly so
// a forecast starting tomorrow still yields the correct days-until-breach.
export function calcRunway(dailyData, today, bufferThreshold = 0) {
  const future = dailyData.filter(d => !d.isBeforeToday);
  const breach = future.find(d => d.balance < bufferThreshold);
  if (!breach) return Infinity;
  const breachDate = new Date(breach.year, breach.month - 1, breach.day);
  const diff = Math.floor((breachDate.getTime() - today.getTime()) / 86_400_000);
  return Math.max(0, diff);
}
